import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, MoreVertical, Trash2, Edit, Eye } from 'lucide-react';
import type { Project } from '@/lib/projectService';

interface ProjectCardProps {
  project: Project;
  onEdit?: (project: Project) => void;
  onDelete?: (project: Project) => void;
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/20' },
  completed: { label: 'Completed', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-500/20' },
  on_hold: { label: 'On Hold', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/20' },
  cancelled: { label: 'Cancelled', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-500/20' },
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const status = statusConfig[project.status] || statusConfig.active;
  const clients = project.members?.filter(m => m.role === 'client') || [];
  const hasDuration = project.start_date || project.end_date;

  return (
    <div className="group rounded-xl bg-card border border-border shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
      {/* Cover Image */}
      <Link to={`/boards/projects/${project.slug}`}>
        <div
          className="h-44 relative overflow-hidden"
          style={{
            backgroundImage: project.cover_image
              ? `url(${project.cover_image})`
              : 'linear-gradient(135deg, var(--workspace-accent, #3b82f6) 0%, var(--workspace-accent-hover, #2563eb) 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Status Badge */}
          <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.color}`}>
            {status.label}
          </div>

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="p-2.5 bg-white/20 rounded-full backdrop-blur-sm">
                <Eye className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </Link>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <Link to={`/boards/projects/${project.slug}`} className="min-w-0">
            <h3 className="text-lg font-semibold text-foreground truncate hover:text-workspace-accent transition-colors">
              {project.name}
            </h3>
          </Link>

          {/* Menu */}
          <div className="relative flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-8 z-40 rounded-lg border border-border bg-popover shadow-xl min-w-[160px] overflow-hidden">
                  <button
                    onClick={() => { setMenuOpen(false); onEdit?.(project); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted flex items-center gap-2 transition-colors"
                  >
                    <Edit className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); onDelete?.(project); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Description */}
        {project.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {project.description}
          </p>
        )}

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-muted-foreground">Progress</span>
            <span className="text-xs font-bold text-foreground">{project.progress}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${project.progress}%`,
                background: project.progress === 100
                  ? 'linear-gradient(90deg, #10b981, #059669)'
                  : 'linear-gradient(90deg, var(--workspace-accent, #3b82f6), var(--workspace-accent-hover, #2563eb))',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          {/* Duration */}
          {hasDuration && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {formatDate(project.start_date)}
                {project.start_date && project.end_date && ' — '}
                {formatDate(project.end_date)}
              </span>
            </div>
          )}

          {/* Client Avatars */}
          {clients.length > 0 && (
            <div className="flex items-center gap-1">
              <div className="flex -space-x-2">
                {clients.slice(0, 3).map((client) => (
                  <div
                    key={client.user_id}
                    className="w-7 h-7 rounded-full border-2 border-card bg-workspace-accent/20 flex items-center justify-center text-xs font-semibold text-workspace-accent overflow-hidden"
                    title={client.profile ? `${client.profile.first_name || ''} ${client.profile.last_name || ''}`.trim() : 'Client'}
                  >
                    {client.profile?.avatar_url ? (
                      <img src={client.profile.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      (client.profile?.first_name?.[0] || client.profile?.email?.[0] || 'C').toUpperCase()
                    )}
                  </div>
                ))}
                {clients.length > 3 && (
                  <div className="w-7 h-7 rounded-full border-2 border-card bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                    +{clients.length - 3}
                  </div>
                )}
              </div>
              <Users className="w-3.5 h-3.5 text-muted-foreground ml-1" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
